import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi, type UserPreferences } from '../services/api/auth';

const PREFS_KEY = ['auth', 'preferences'] as const;
const MAILS_KEY = ['auth', 'notification-mails'] as const;

export function usePreferences() {
  return useQuery({ queryKey: PREFS_KEY, queryFn: () => authApi.getPreferences() });
}

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UserPreferences) => authApi.updatePreferences(patch),
    onSuccess: (prefs) => {
      qc.setQueryData(PREFS_KEY, prefs);
      // Mute state is part of the mail list, so it has to be re-read too.
      void qc.invalidateQueries({ queryKey: MAILS_KEY });
    },
  });
}

export function useNotificationMails() {
  return useQuery({ queryKey: MAILS_KEY, queryFn: () => authApi.getNotificationMails() });
}

/**
 * Renames the signed-in user. The `auth/me` query is invalidated so the header,
 * the account menu and Settings all pick the new name up at once (QA ledger
 * AUTH-011).
 */
export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fullName: string) => authApi.updateProfile(fullName),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['auth'] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (v: { currentPassword: string; newPassword: string }) => {
      const res = await authApi.changePassword(v.currentPassword, v.newPassword);
      if (!res.success) {
        throw new Error(res.error || 'Failed to change password');
      }
    },
  });
}
